import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Header } from "@/components/landing/Header";
import { Hero } from "@/components/landing/Hero";
import { CompanySnapshot } from "@/components/landing/CompanySnapshot";
import { Ecosystem } from "@/components/landing/Ecosystem";
import { Milestones } from "@/components/landing/Milestones";
import { RevenueGrowth } from "@/components/landing/RevenueGrowth";
import { TrinityModel } from "@/components/landing/TrinityModel";
import { TechnicalCommercialCapabilities } from "@/components/landing/TechnicalCommercialCapabilities";
import { Solutions } from "@/components/landing/Solutions";
import { GlobalPresence } from "@/components/landing/GlobalPresence";
import { Team } from "@/components/landing/Team";
import { ProofAreas } from "@/components/landing/ProofAreas";
import { PartnershipModels } from "@/components/landing/PartnershipModels";
import { Partnership } from "@/components/landing/Partnership";
import { PhisonPartnership } from "@/components/landing/PhisonPartnership";
import { Footer } from "@/components/landing/Footer";
import { CONTENT_VERSION, getEditableSeedContent, getLocalizedContentFromEditable, normalizeEditableContent } from "@/data/trinityI18n";
import { fetchSiteContent } from "@/lib/contentApi";
import { getStoredLanguage, storeLanguage } from "@/lib/languageStorage";
import AdminEditor from "@/pages/AdminEditor";
import { Toaster } from "@/components/ui/sonner";
import { captureFirstTouchAttribution } from "@/lib/utmAttribution";

const PUBLIC_LANGUAGE_STORAGE_KEY = "trinity_public_language";

const renderOrderedSection = (sectionKey, content, language) => {
  const sections = {
    hero: <Hero content={content} />,
    snapshot: <CompanySnapshot content={content.snapshot} media={content.sectionMedia?.snapshot} />,
    ecosystem: <Ecosystem content={content.ecosystem} media={content.sectionMedia?.ecosystem} />,
    milestones: <Milestones content={content.milestones} media={content.sectionMedia?.milestones} />,
    revenue: <RevenueGrowth content={content.revenue} />,
    trinityModel: <TrinityModel positioning={content.positioning} operatingModel={content.operatingModel} media={content.sectionMedia?.trinityModel} />,
    proof: <ProofAreas content={content.proof} images={content.proofImages} logos={content.clientLogos} />,
    capabilities: <TechnicalCommercialCapabilities capabilities={content.capabilities} cameraExpertise={content.cameraExpertise} media={content.sectionMedia?.capabilities} />,
    solutions: <Solutions content={content.solutions} media={content.sectionMedia?.solutions} />,
    global: <GlobalPresence content={content.global} mapRegions={content.mapRegions} />,
    phison: <PhisonPartnership content={content.phison} media={content.sectionMedia?.phison} />,
    team: <Team content={content.team} images={content.teamImages} />,
    partnershipModels: <PartnershipModels content={content.partnershipModels} media={content.sectionMedia?.partnershipModels} />,
    partnership: <Partnership content={content.partnership} language={language} />,
  };
  return sections[sectionKey] || null;
};

const Home = () => {
  const [language, setLanguage] = useState(() => getStoredLanguage(PUBLIC_LANGUAGE_STORAGE_KEY));
  const [editableContent, setEditableContent] = useState(getEditableSeedContent());
  const content = useMemo(() => getLocalizedContentFromEditable(language, editableContent), [language, editableContent]);

  const handleLanguageChange = (nextLanguage) => {
    storeLanguage(PUBLIC_LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguage(nextLanguage);
  };

  useEffect(() => {
    captureFirstTouchAttribution();
    fetchSiteContent()
      .then((contentData) => {
        if (contentData && Object.keys(contentData).length > 0) {
          setEditableContent(normalizeEditableContent(contentData));
        }
      })
      .catch(() => {});
  }, []);

  const visibleSections = useMemo(() => {
    const sectionOrder = content.sectionOrder || ["hero", "snapshot", "ecosystem", "milestones", "trinityModel", "revenue", "proof", "capabilities", "solutions", "global", "phison", "team", "partnershipModels", "partnership"];
    const hiddenSections = content.hiddenSections || [];
    return sectionOrder.filter((sectionKey) => !hiddenSections.includes(sectionKey));
  }, [content.hiddenSections, content.sectionOrder]);

  return (
    <main data-testid="trinity-landing-page" dir={content.dir} lang={language}>
      <Header content={content} language={language} onLanguageChange={handleLanguageChange} visibleSections={visibleSections} />
      {visibleSections.map((sectionKey) => {
        const section = renderOrderedSection(sectionKey, content, language);
        return section ? <div key={sectionKey} data-testid={`section-wrapper-${sectionKey}`}>{section}</div> : null;
      })}
      <Footer content={content.footer} />
      <Toaster position="top-right" richColors />
    </main>
  );
};

function App() {
  if (window.location.pathname === "/admin") {
    return <AdminEditor />;
  }
  return <Home />;
}

export default App;
